// Currency symbols mapping
const currencySymbols = {
  'USD': '$',
  'EUR': '€',
  'GBP': '£',
  'JPY': '¥',
  'ILS': '₪',
  'CAD': 'C$',
  'AUD': 'A$',
  'CHF': 'CHF',
  'SEK': 'kr',
  'NOK': 'kr',
  'DKK': 'kr',
  'PLN': 'zł',
  'CZK': 'Kč',
  'HUF': 'Ft',
  'BGN': 'лв',
  'RON': 'lei',
  'HRK': 'kn',
  'RUB': '₽',
  'TRY': '₺',
  'BRL': 'R$',
  'MXN': 'Mex$',
  'ARS': 'AR$',
  'CLP': 'CLP$',
  'COP': 'COL$',
  'PEN': 'S/',
  'CNY': '¥',
  'INR': '₹',
  'KRW': '₩',
  'SGD': 'S$',
  'MYR': 'RM',
  'THB': '฿',
  'PHP': '₱',
  'IDR': 'Rp',
  'VND': '₫',
  'ZAR': 'R',
  'EGP': 'E£',
  'MAD': 'DH',
  'NGN': '₦',
  'KES': 'KSh',
};

// Currencies where the symbol comes after the amount
const symbolAfterAmount = new Set(['SEK', 'NOK', 'DKK', 'PLN', 'CZK', 'HUF', 'BGN', 'RON', 'HRK']);

// AniPaw's provider marketplace currently operates only in Israel.
// Service prices must not depend on browser locale or incomplete account data.
export const PROVIDER_SERVICE_CURRENCY_CODE = 'ILS';

/**
 * Get currency symbol for a given currency code
 * @param {string} currencyCode - The currency code (e.g., 'USD', 'EUR')
 * @returns {string} The currency symbol
 */
export const getCurrencySymbol = (currencyCode) => {
  if (!currencyCode) return '$'; // Default to USD
  return currencySymbols[currencyCode.toUpperCase()] || currencyCode;
};

/**
 * Format price with currency symbol
 * @param {number} amount - The price amount
 * @param {string} currencyCode - The currency code
 * @param {number} decimalPlaces - Number of decimal places (default: 2)
 * @returns {string} Formatted price with currency
 */
export const formatPrice = (amount, currencyCode = 'USD', decimalPlaces = 2) => {
  const code = currencyCode?.toUpperCase() || 'USD';
  const symbol = getCurrencySymbol(code);
  const formattedAmount = Number(amount).toFixed(decimalPlaces);
  
  if (symbolAfterAmount.has(code)) {
    return `${formattedAmount} ${symbol}`;
  } else {
    return `${symbol}${formattedAmount}`;
  }
};

/**
 * Resolve the currency code for an order request. Prefers the order's own
 * currencyCode (set by the backend from the provider's service), then falls back
 * to the linked service and any assigned provider's service, then USD.
 * @param {object} order - OrderRequest object
 * @returns {string} Currency code
 */
export const resolveOrderCurrency = (order) => {
  if (!order) return PROVIDER_SERVICE_CURRENCY_CODE;
  if (order.currencyCode) return order.currencyCode;
  const svc = order.providerDogService;
  if (svc && svc.currencyCode) return svc.currencyCode;
  const providers = order.sentToProviders || [];
  for (const p of providers) {
    for (const s of (p.services || [])) {
      if (s && s.currencyCode) return s.currencyCode;
    }
  }
  return PROVIDER_SERVICE_CURRENCY_CODE;
};

/**
 * Format service price for display
 * @param {object} service - Service object with price and currencyCode
 * @returns {string} Formatted price
 */
export const formatServicePrice = (service) => {
  return formatPrice(
    service.price,
    service.currencyCode || service.currency_code || PROVIDER_SERVICE_CURRENCY_CODE,
  );
};

/**
 * Format product price for display
 * @param {object} product - Product object with price and currencyCode
 * @returns {string} Formatted price
 */
export const formatProductPrice = (product) => {
  return formatPrice(product.price, product.currencyCode || product.currency_code);
};

/**
 * Check if currency code is supported
 * @param {string} currencyCode - The currency code to check
 * @returns {boolean} True if supported
 */
export const isSupportedCurrency = (currencyCode) => {
  return currencySymbols.hasOwnProperty(currencyCode?.toUpperCase());
};

/**
 * Get all supported currency codes
 * @returns {Array} Array of supported currency codes
 */
export const getSupportedCurrencies = () => {
  return Object.keys(currencySymbols);
};

/**
 * Get currency display name
 * @param {string} currencyCode - The currency code
 * @returns {string} Display name for the currency
 */
export const getCurrencyDisplayName = (currencyCode) => {
  const names = {
    'USD': 'US Dollar',
    'EUR': 'Euro',
    'GBP': 'British Pound',
    'JPY': 'Japanese Yen',
    'ILS': 'Israeli Shekel',
    'CAD': 'Canadian Dollar',
    'AUD': 'Australian Dollar',
    'CHF': 'Swiss Franc',
    // Add more as needed
  };
  return names[currencyCode?.toUpperCase()] || currencyCode;
};
